import { Routes } from '@angular/router';
import { AccessDeniedComponent, ServerErrorComponent } from '@bodyanalytics/ui-core';
import { LoginComponent } from '@bodyanalytics/authentication-ui';
import { adminDeepLinkGuard } from './guards/admin-deeplink.guard';

// Lane F (#2198): route-level code splitting. Every admin feature is now
// loadChildren'd instead of statically imported, following the calendar
// app's lazy-route pattern (apps/front/calendar/src/app/app.routes.ts):
// the concrete URL segment stays on a STATIC outer route so the router can
// match without importing the chunk, and the dynamically-imported route is
// spread with `path: ''` since it already carries that same segment inside
// the feature lib (unlike calendar-ui's routes, which are authored relative
// to their mount point already). Keeping the segment on the outer route also
// keeps it visible to app.routes.spec.ts's synchronous `collectPaths` walk,
// which cannot see inside an unresolved `loadChildren` import.
export default [
  // Public / pre-auth routes — reachable without an admin role.
  // #2119: the pre-auth landing is the shared, theme-aware LoginComponent
  // (its `isAdminPortal()` branch renders the staff sign-in). The bespoke
  // AdminHomeComponent duplicate was deleted — admin converges on shared
  // login exactly like calendar/customer did in #1929/#1959.
  { path: 'login', component: LoginComponent },
  { path: '', pathMatch: 'full', redirectTo: 'login' },
  // Deep-link compat: '/home' was the old pre-auth landing URL.
  { path: 'home', redirectTo: 'login' },
  { path: 'error', component: ServerErrorComponent },
  // ui-core is a core shared lib statically imported across the app, so it
  // cannot also be lazy-loaded (enforce-module-boundaries). Route directly.
  { path: 'access-denied', component: AccessDeniedComponent },
  // Onboarding / auth flows stay outside the admin-role gate: a brand-new admin
  // has no stored role yet, and register/setup/license are how they obtain one.
  {
    path: 'auth/admin-register',
    loadChildren: () =>
      import('@bodyanalytics/front/admin/registration/feature').then(mod => [
        { ...mod.adminRegisterUiRoutes, path: '' },
      ]),
  },
  {
    path: 'auth/admin-setup',
    loadChildren: () => import('@bodyanalytics/admin-setup-ui').then(mod => [{ ...mod.setupUiRoutes[0], path: '' }]),
  },
  {
    path: 'auth/license',
    loadChildren: () =>
      import('@bodyanalytics/front/admin/license/feature').then(mod => [{ ...mod.manageLicenseUiRoutes, path: '' }]),
  },
  // Tenant-data management — gated to ADMIN/OPERATOR roles (#1264). Before this
  // guard any authenticated Auth0 user could reach these routes.
  //
  // #2303: adminDeepLinkGuard wraps AdminRoleGuard so an unauthenticated hard
  // reload of a deep link captures state.url as Auth0's appState.target
  // before delegating the role check — see its doc comment for why.
  {
    path: '',
    canActivateChild: [adminDeepLinkGuard],
    children: [
      {
        path: 'auth/admin-dashboard',
        loadChildren: () =>
          import('@bodyanalytics/admin-dashboard-ui').then(mod => [{ ...mod.adminDashboardUiRoute, path: '' }]),
      },
      {
        path: 'auth/manage-bodymeasurements',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-bodymeasurements/feature').then(mod => [
            { ...mod.manageBodymeasurementsRoute, path: '' },
          ]),
      },
      {
        path: 'auth/manage-clubs',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-clubs/feature').then(mod => [
            { ...mod.manageClubsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-leaderboards',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-leaderboards/feature').then(mod => [
            { ...mod.manageLeaderboardsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-operators',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-operators/feature').then(mod => [
            { ...mod.manageOperatorsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-customers',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-customers/feature').then(mod => [
            { ...mod.manageCustomersUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-bookings',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-bookings/feature').then(mod => [
            { ...mod.manageBookingUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-orders',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-orders/feature').then(mod => [
            { ...mod.manageOrdersUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-regions',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-regions/feature').then(mod => [
            { ...mod.manageRegionsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-settings',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-settings/feature').then(mod => [
            { ...mod.manageSettingsRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-payments',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-payments/feature').then(mod => [
            { ...mod.managePaymentsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-financial-reports',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-financial-reports/feature').then(mod => [
            { ...mod.manageFinancialReportsRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-discounts',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-discounts/feature').then(mod => [
            { ...mod.manageDiscountsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-contact-requests',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-contact-requests/feature').then(mod => [
            { ...mod.manageContactRequestsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-services',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-services/feature').then(mod => [
            { ...mod.manageServicesUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-events',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-events/feature').then(mod => [
            { ...mod.manageEventsUiRoutes[0], path: '' },
          ]),
      },
      {
        path: 'auth/manage-event-waitlist',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-waitlist/feature').then(mod => [
            { ...mod.manageWaitlistRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/manage-reservations',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-reservations/feature').then(mod => [
            { ...mod.manageReservationsUiRoutes, path: '' },
          ]),
      },
      {
        path: 'tenants',
        loadChildren: () =>
          import('@bodyanalytics/front/admin/manage-tenant/feature').then(mod => [
            { ...mod.manageTenantUiRoutes, path: '' },
          ]),
      },
      {
        path: 'auth/profile',
        loadChildren: () => import('@bodyanalytics/profile-ui').then(mod => [{ ...mod.profileUiRoutes[0], path: '' }]),
      },
    ],
  },
  { path: '**', redirectTo: 'login' },
] satisfies Routes;
